import '../css/style.styl'
import 'reset-css'
import 'regenerator-runtime/runtime'

import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'

import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { NodePass } from 'three/examples/jsm/nodes/postprocessing/NodePass.js';
import * as Nodes from 'three/examples/jsm/nodes/Nodes.js';

import ViewerGUI from './gui'

import DebugInfo from './debugInfo'
import {loadGLTF, loadRGBE, loadFBX, replaceMaterial, loadCubeMap} from './resourceLoader'

import Params from './parameter'
import { Vector3 } from 'three'

window.onload = ()=>{
  const app = new App();
  app.init();
}



class App{
  constructor(){
   
    this.debugInfo = new DebugInfo();
  }

  async init(){ 
    // setup renderer
    this.renderer = new THREE.WebGLRenderer( { antialias: true } );
    this.renderer.setPixelRatio( window.devicePixelRatio );
    this.renderer.setSize( window.innerWidth, window.innerHeight );
    this.renderer.outputEncoding = THREE.sRGBEncoding;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.autoUpdate = false;
    this.renderer.shadowMap.needsUpdate = true; // create shadow map at once (needsUpdate will turn false automatically on next frame)
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.info.autoReset = false;
     
    
    document.body.appendChild( this.renderer.domElement );


    // create camera
    this.camera = new THREE.PerspectiveCamera( 45, window.innerWidth/window.innerHeight, 0.25, 10000);
    const camPos = new THREE.Vector3(20, 20, 10);
    this.camera.position.set(camPos.x, camPos.y, camPos.z);

    // create scene
    this.scene = new THREE.Scene();

    // render pass (posteffect etc.)
    this.setupRenderPass();
    
    // set up scene objects (wait loading assets before starting rendering)
    await this.initScene();

    // clock
    this.clock = new THREE.Clock();

    // Viewer GUI
    const viewerGUI = new ViewerGUI(this.camera);

    // execute render loop
    this.render();
    window.addEventListener( 'resize', this.onWindowResize.bind(this), false );
  } // end of init()

  onWindowResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();

    this.renderer.setSize( window.innerWidth, window.innerHeight );
    this.composer.setSize( window.innerWidth, window.innerHeight );
  }

  render(){
    const loop = ()=>{
      requestAnimationFrame( loop );

      const delta = this.clock.getDelta();

      this.controls.update();
      this.debugInfo.update(this.scene, this.renderer);
      this.nodeFrame.update(delta).updateNode(this.nodepass.material);
      this.composer.render();
    }
    loop();
  }

  async initScene(){
    // scene params
    const shadowBias = -0.00001;

    // put directional light
    this.dirLight = new THREE.DirectionalLight( 0xffffff, 1);
    this.dirLight.position.set( -400, 300, 400 );
    this.dirLight.castShadow = Params.bEnableDirLightShadow;
    this.dirLight.shadow.camera.near = 1;
    this.dirLight.shadow.camera.far = 1000;
    this.dirLight.shadow.camera.right = 100;
    this.dirLight.shadow.camera.left = -100;
    this.dirLight.shadow.camera.top	= 100;
    this.dirLight.shadow.camera.bottom = -100;
    this.dirLight.shadow.mapSize.width = 1024;
    this.dirLight.shadow.mapSize.height = 1024;
    this.dirLight.shadow.bias = shadowBias;
    this.dirLight.visible = Params.bEnableDirLight;
    this.dirLight.intensity = Params.dirLightIntensity;
    this.scene.add(this.dirLight);

    this.ambientLight = new THREE.AmbientLight( 0xffffff );
    this.ambientLight.intensity = Params.ambientLightIntensity;
    this.ambientLight.visible = Params.bEnableAmbientLight;
    this.scene.add(this.ambientLight);

    this.debugInfo.checkTime("start DL env map");

    // load environment map
    this.debugInfo.checkTime("start DL background dome");
    this.loadBackground();
    this.debugInfo.checkTime("env DL background dome");
    

    this.debugInfo.checkTime("start DL CUBE MAP");
    const cubeMap = await loadCubeMap(Params.cubemapPath, '.png');
    this.cubeMap = cubeMap;
    this.debugInfo.checkTime("end DL CUBE MAP");

    // control
    this.controls = new OrbitControls( this.camera, this.renderer.domElement );
    this.controls.minDistance = 2;
    this.controls.maxDistance = 10000;
    const camTarget = new Vector3(0, 10, 0);
    this.controls.target.set( camTarget.x, camTarget.y, camTarget.z );


    this.debugInfo.checkTime("start load model");
    await this.loadModel(Params.modelPath);
    this.debugInfo.checkTime("end load model");

    // helper for debug
    // this.scene.add( new THREE.CameraHelper( this.dirLight.shadow.camera ) );
  }

  setupRenderPass(){
    // composer
    this.composer = new EffectComposer(this.renderer);

    this.composer.addPass(new RenderPass(this.scene, this.camera));

    // ssao pass
    // this.ssaoPass = new SSAOPass(this.scene, this.camera, window.innerWidth, window.innerHeight);
    // this.ssaoPass.kernelRadius = 16;
    // this.composer.addPass(this.ssaoPass);

    // bloom
    this.bloomPass = new UnrealBloomPass( new THREE.Vector2( window.innerWidth, window.innerHeight ), 1.5, 0.4, 0.85 );
    // this.bloomPass.threshold = Params.bloomParams.bloomThreshold;
    // this.bloomPass.strength = Params.bloomParams.bloomStrength;
    // this.bloomPass.radius = Params.bloomParams.bloomRadius;
    // this.composer.addPass(this.bloomPass);

    // SMAAA
    this.smaaPass = new SMAAPass(window.innerWidth * this.renderer.getPixelRatio(), window.innerHeight * this.renderer.getPixelRatio());
    // this.composer.addPass(this.smaaPass);

    // ColorCorrection
    this.nodeFrame = new Nodes.NodeFrame();
    this.nodepass = new NodePass();
    // this.composer.addPass( this.nodepass );
  }

  async loadBackground(){
    const obj = await loadGLTF('/assets/background.glb');
    const model = obj.scene;
    
    model.traverse((child)=>{ 
      if(child.isMesh){
        replaceMaterial(child, 'MeshBasicMaterial');
      }
    });
     // add to scene
     this.scene.add(model);  
  }
  
  async loadModel(modelPath){
    const isGltf = modelPath.match(/\.(gltf|glb)$/) != null
    const isFbx = modelPath.match(/\.(fbx)$/) != null

    let model = null;
    if(isGltf){
      const obj = await loadGLTF(modelPath)
      model = obj.scene;
    }else if(isFbx){
      model = await loadFBX(modelPath);
    }else{
      return;
    }
    model.traverse((child)=>{ 
      if(child.isMesh){
        child.material.emissive = 0;
        child.material.envMapIntensity = Params.envIntensity;

        if(Params.bEnableDirLightShadow){
          if(child.material.opacity < 1){
            child.castShadow = false;
            child.receiveShadow = false;
          }else{
            child.castShadow = true;
            child.receiveShadow = true;
          }
        }

        child.material.envMap = this.cubeMap;

        if(Params.defaultMaterial != 'MeshStandardMaterial')
          replaceMaterial(child, Params.defaultMaterial);
      }
    });

    // add to scene
    this.scene.add(model);  
  }
}

